'use client';

import { token } from '@terra/ui-styled-system';
import { MetaDefault } from '@terra/ui-design-system';
import {
  Alert01Icon,
  PlayIcon,
  Shapes01Icon,
  Target03Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useKpiMetrics } from '../../hooks/dashboard/useKpiMetrics';
import { useSinceLastLogin } from '../../hooks/dashboard/useSinceLastLogin';
import { useTargetType } from '../../store/projectStore';
import { formatNumber } from '../../utils/formatNumber';
import { DashboardStripCard } from './DashboardStripCard';
import {
  buildSinceLastVisitCells,
  listNewFindingSeverities,
  type SinceLastVisitCell,
  type SinceLastVisitCellKey,
} from './since-last-visit-cells';
import { StripMetricCell } from './StripMetricCell';
import { useOpenListFromHome } from './useOpenListFromHome';

type SinceLastVisitStripProps = {
  applicationId: string;
  baselineAt: Date | null;
  isBaselineReady: boolean;
};

const CELL_ICONS: Record<SinceLastVisitCellKey, ReactNode> = {
  findings: <Alert01Icon size={16} color={token('colors.fg.neutral.muted')} />,
  signals: <Target03Icon size={16} color={token('colors.fg.neutral.muted')} />,
  assets: <Shapes01Icon size={16} color={token('colors.fg.neutral.muted')} />,
  executions: <PlayIcon size={16} color={token('colors.fg.neutral.muted')} />,
};

const CELL_LABEL_KEYS = {
  withBaseline: {
    findings: 'newFindings',
    signals: 'newSignals',
    assets: 'newAssets',
    executions: 'newExecutions',
  },
  withoutBaseline: {
    findings: 'findings',
    signals: 'signals',
    assets: 'assets',
    executions: 'executions',
  },
} as const satisfies Record<string, Record<SinceLastVisitCellKey, string>>;

export function SinceLastVisitStrip({
  applicationId,
  baselineAt,
  isBaselineReady,
}: SinceLastVisitStripProps) {
  const t = useTranslations('home.sinceLastVisit');
  const targetType = useTargetType();
  const { data: metrics, isLoading: isLoadingTotals } =
    useKpiMetrics(applicationId);
  const { data: deltas, isLoading: isLoadingDeltas } = useSinceLastLogin({
    applicationId,
    targetType,
    since: baselineAt,
  });
  const { openList, openListContextMenu } = useOpenListFromHome({
    applicationId,
  });

  const isLoading =
    !isBaselineReady ||
    isLoadingTotals ||
    (baselineAt !== null && isLoadingDeltas);
  const hasBaseline = baselineAt !== null && deltas !== undefined;
  const cells = buildSinceLastVisitCells({
    baselineAt,
    totals: {
      findings: metrics?.findings.total ?? 0,
      signals: metrics?.signals.total ?? 0,
      assets: metrics?.assets.total ?? 0,
      executions: metrics?.executions.total ?? 0,
    },
    deltas,
  });
  const labelKeys = hasBaseline
    ? CELL_LABEL_KEYS.withBaseline
    : CELL_LABEL_KEYS.withoutBaseline;

  return (
    <DashboardStripCard
      title={t('title')}
      trailingContent={
        !isLoading && !hasBaseline ? (
          <MetaDefault color="fg.neutral.muted">{t('noBaseline')}</MetaDefault>
        ) : null
      }
      columns={4}
      cells={cells.map((cell) => ({
        key: cell.key,
        onClick: () => openList(cell.destination),
        onContextMenu: (event) => openListContextMenu(event, cell.destination),
        content: (
          <StripMetricCell
            label={t(labelKeys[cell.key])}
            icon={CELL_ICONS[cell.key]}
            value={cell.value}
            isLoading={isLoading}
            secondary={hasBaseline ? <CellSecondary cell={cell} /> : null}
          />
        ),
      }))}
    />
  );
}

function CellSecondary({ cell }: { cell: SinceLastVisitCell }) {
  const t = useTranslations('home.sinceLastVisit');
  const tSeverity = useTranslations('home.posture.severity');
  const severities = cell.severity
    ? listNewFindingSeverities(cell.severity)
    : [];

  if (severities.length > 0) {
    return (
      <MetaDefault color="fg.neutral.error">
        {severities
          .map(({ severity, count }) =>
            t('severityCount', {
              count: formatNumber(count),
              severity: tSeverity(severity),
            }),
          )
          .join(' · ')}
      </MetaDefault>
    );
  }

  return (
    <MetaDefault color="fg.neutral.subtle">
      {t('ofTotal', { total: formatNumber(cell.total) })}
    </MetaDefault>
  );
}
